import { useInputControl } from '@conform-to/react'
import { REGEXP_ONLY_DIGITS_AND_CHARS, type OTPInputProps } from 'input-otp'
import React, { useId } from 'react'
import { HoneypotInputs } from 'remix-utils/honeypot/react'
import { Checkbox, type CheckboxProps } from './ui/checkbox.tsx'
import {
	InputOTP,
	InputOTPGroup,
	InputOTPSeparator,
	InputOTPSlot,
} from './ui/input-otp.tsx'
import { Input } from './ui/input.tsx'
import { Label } from './ui/label.tsx'
import { Textarea } from './ui/textarea.tsx'

export type ListOfErrors = Array<string | null | undefined> | null | undefined

/**
 * The dashboard's raw-field class, for the surfaces that drive a plain
 * `<input>` / `<select>` / `<textarea>` rather than going through `Field`.
 *
 * This string was copy-pasted into six route files as a local `dashFieldClass`
 * / `tokenFieldClass` / `integrationFieldClass`, and every copy carried the same
 * bug: `border-border`. `--border` measures 1.27:1 against `--card` in dark and
 * 1.35:1 in light, which is fine for a rule that divides two things and fails
 * WCAG 1.4.11 for the edge that is the only thing telling you a control is
 * there - the fill supplies no help, since `--background` on `--card` is about
 * 1.05:1. `border-input` is the token that carries the 3:1 obligation. See The
 * Control-Edge Rule in DESIGN.md.
 *
 * No width: four of the six call sites want `w-full` and two size to content, so
 * they append it. Anything appended here wins normally - these are Tailwind
 * utilities in the same layer, resolved by `cn()` at the call site.
 */
export const dashFieldClass =
	'flex h-10 rounded-md border border-input bg-background px-3 text-[14px] text-foreground placeholder:text-muted-foreground/60 focus:border-primary focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:ring-offset-background'

/**
 * The label above one of those fields. A field label is not an eyebrow: it runs
 * 0.16em where the section eyebrow runs 0.22em, and it names a control rather
 * than opening a section. Kept separate from `.eyebrow` on purpose.
 */
export const dashLabelClass =
	'mb-1.5 block font-[family-name:var(--font-mono)] text-[11px] uppercase tracking-[0.16em] text-muted-foreground'

/**
 * The reserved slot under a field, so an arriving error does not shove the
 * submit button down the page. It was `min-h-[32px] px-4 pt-1 pb-3`, which
 * reserved 32px for a 16px message and indented it 16px past the field's own
 * left edge - a permanent gap on every clean form, and errors that did not line
 * up with the control they belonged to. 20px matches the 5-unit rhythm the auth
 * fields already use between one field and the next.
 */
const ERROR_SLOT = 'min-h-5 pt-1.5'

/**
 * `announce` is for form-level errors - the ones that appear after a submit
 * that failed for a reason no single field owns ("Invalid email or password").
 * Those swap into the DOM while focus is still on the input the user was in,
 * so without a live region nothing tells a screen-reader user the attempt was
 * rejected. Field-level errors leave it off on purpose: conform already wires
 * them through `aria-describedby`, and revalidating on blur would otherwise
 * interrupt with an alert on every field the user leaves.
 */
export function ErrorList({
	id,
	errors,
	announce = false,
}: {
	errors?: ListOfErrors
	id?: string
	announce?: boolean
}) {
	const errorsToRender = errors?.filter(Boolean)
	if (!errorsToRender?.length) return null
	return (
		<ul
			id={id}
			role={announce ? 'alert' : undefined}
			className="flex flex-col gap-1"
		>
			{errorsToRender.map((e) => (
				<li key={e} className="text-destructive text-[11px]">
					{e}
				</li>
			))}
		</ul>
	)
}

export function Field({
	labelProps,
	inputProps,
	errors,
	className,
}: {
	labelProps: React.LabelHTMLAttributes<HTMLLabelElement>
	inputProps: React.InputHTMLAttributes<HTMLInputElement>
	errors?: ListOfErrors
	className?: string
}) {
	const fallbackId = useId()
	const id = inputProps.id ?? fallbackId
	const errorId = errors?.length ? `${id}-error` : undefined
	return (
		<div className={className}>
			<Label htmlFor={id} {...labelProps} />
			<Input
				id={id}
				aria-invalid={errorId ? true : undefined}
				aria-describedby={errorId}
				{...inputProps}
			/>
			<div className={ERROR_SLOT}>
				{errorId ? <ErrorList id={errorId} errors={errors} /> : null}
			</div>
		</div>
	)
}

export function OTPField({
	labelProps,
	inputProps,
	errors,
	className,
}: {
	labelProps: React.LabelHTMLAttributes<HTMLLabelElement>
	inputProps: Partial<OTPInputProps & { render: never }>
	errors?: ListOfErrors
	className?: string
}) {
	const fallbackId = useId()
	const id = inputProps.id ?? fallbackId
	const errorId = errors?.length ? `${id}-error` : undefined
	return (
		<div className={className}>
			<Label htmlFor={id} {...labelProps} />
			<InputOTP
				pattern={REGEXP_ONLY_DIGITS_AND_CHARS}
				maxLength={6}
				id={id}
				aria-invalid={errorId ? true : undefined}
				aria-describedby={errorId}
				containerClassName={errorId ? 'group is-invalid' : 'group'}
				{...inputProps}
			>
				<InputOTPGroup>
					<InputOTPSlot index={0} />
					<InputOTPSlot index={1} />
					<InputOTPSlot index={2} />
				</InputOTPGroup>
				<InputOTPSeparator />
				<InputOTPGroup>
					<InputOTPSlot index={3} />
					<InputOTPSlot index={4} />
					<InputOTPSlot index={5} />
				</InputOTPGroup>
			</InputOTP>
			{/* Announced, unlike the other fields: a one-time code is only ever
			    checked on the server, so the rejection swaps in while focus is
			    still in the slots and there is no blur-revalidation to interrupt. */}
			<div className={ERROR_SLOT}>
				{errorId ? <ErrorList id={errorId} errors={errors} announce /> : null}
			</div>
		</div>
	)
}

export function TextareaField({
	labelProps,
	textareaProps,
	errors,
	className,
}: {
	labelProps: React.LabelHTMLAttributes<HTMLLabelElement>
	textareaProps: React.TextareaHTMLAttributes<HTMLTextAreaElement>
	errors?: ListOfErrors
	className?: string
}) {
	const fallbackId = useId()
	const id = textareaProps.id ?? textareaProps.name ?? fallbackId
	const errorId = errors?.length ? `${id}-error` : undefined
	return (
		<div className={className}>
			<Label htmlFor={id} {...labelProps} />
			<Textarea
				id={id}
				aria-invalid={errorId ? true : undefined}
				aria-describedby={errorId}
				{...textareaProps}
			/>
			<div className={ERROR_SLOT}>
				{errorId ? <ErrorList id={errorId} errors={errors} /> : null}
			</div>
		</div>
	)
}

export function CheckboxField({
	labelProps,
	buttonProps,
	errors,
	className,
}: {
	labelProps: React.ComponentProps<'label'>
	buttonProps: Omit<CheckboxProps, 'type'> & {
		name: string
		form: string
		value?: string
		type?: string // Accept but ignore the type prop from Conform
	}
	errors?: ListOfErrors
	className?: string
}) {
	const { key, defaultChecked, type, ...checkboxProps } = buttonProps
	const fallbackId = useId()
	const checkedValue = buttonProps.value ?? 'on'
	const input = useInputControl({
		key,
		name: buttonProps.name,
		formId: buttonProps.form,
		initialValue: defaultChecked ? checkedValue : undefined,
	})
	const id = buttonProps.id ?? fallbackId
	const errorId = errors?.length ? `${id}-error` : undefined

	return (
		<div className={className}>
			<div className="flex gap-2">
				<Checkbox
					{...checkboxProps}
					id={id}
					aria-invalid={errorId ? true : undefined}
					aria-describedby={errorId}
					checked={input.value === checkedValue}
					onCheckedChange={(state) => {
						input.change(state.valueOf() ? checkedValue : '')
						buttonProps.onCheckedChange?.(state)
					}}
					onFocus={(event) => {
						input.focus()
						buttonProps.onFocus?.(event)
					}}
					onBlur={(event) => {
						input.blur()
						buttonProps.onBlur?.(event)
					}}
					type="button"
				/>
				<label
					htmlFor={id}
					{...labelProps}
					className="text-body-xs text-muted-foreground self-center"
				/>
			</div>
			<div className={ERROR_SLOT}>
				{errorId ? <ErrorList id={errorId} errors={errors} /> : null}
			</div>
		</div>
	)
}

/**
 * Honeypot fields, hidden.
 *
 * Stock `HoneypotInputs` renders an inline `<style>` holding its own hiding
 * rule. That rule is dead weight here - it targets `.__honeypot_inputs` and
 * we pass our own class, hidden from `tailwind.css` - but it still cost us a
 * CSP violation on every render React does client-side, where there is no
 * nonce to stamp on it. `patches/remix-utils@8.8.0.patch` drops the element;
 * the hiding is unaffected because it never came from there.
 */
export function Honeypot() {
	return <HoneypotInputs className="honeypot-fields" />
}
